import { ref, onUnmounted } from "vue";
import { useQuizMatchesStore } from "@/stores/storeQuizMatches";
import { useRequestsStore } from "@/stores/storeRequests";

const STORAGE_KEY = "game_match3_history";
const remainingTime = ref("00:00:00");
const isPaused = ref(false);
const gameData = ref(null);
let timer = null;

export function useGameHistory() {
	// ---------- utils ----------
	const pad = (n) => String(n).padStart(2, "0");

	const formatSeconds = (totalSeconds) => {
		const safe = Math.max(Math.floor(totalSeconds), 0);
		const h = Math.floor(safe / 3600);
		const m = Math.floor((safe % 3600) / 60);
		const s = safe % 60;
		return `${pad(h)}:${pad(m)}:${pad(s)}`;
	};

	// ---------- storage ----------
	const saveGameData = (data) => {
		if (typeof window === "undefined") return;
		const stored = {
			token: data.token,
			gift_expires_at: data.gift_expires_at,
			is_timer_paused: data.is_timer_paused,
			remaining_seconds: false,
			savedAt: data.gift_expires_at,
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
		gameData.value = stored;
	};

	const getStoredGameData = () => {
		if (typeof window === "undefined") return null;
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	};

	const clearGameData = () => {
		if (typeof window === "undefined") return;
		localStorage.removeItem(STORAGE_KEY);
		gameData.value = null;
	};

	const getToken = () => {
		if (typeof window === "undefined") return null;
		const stored = getStoredGameData();
		return stored?.token || null;
	};

	// ---------- timer ----------
	// Для неавторизованных - от gift_expires_at (Date.now())
	const startTimerFromExpiryDate = (expiresAt) => {
		stopTimer();

		const update = () => {
			const now = Date.now();
			const expiresMs = new Date(expiresAt).getTime();
			const secondsLeft = Math.floor((expiresMs - now) / 1000);

			if (secondsLeft <= 0) {
				remainingTime.value = "00:00:00";
				stopTimer();
				// clearGameData();
				return;
			}

			remainingTime.value = formatSeconds(secondsLeft);
		};

		update();
		timer = setInterval(update, 1000);
	};

	// Для авторизованных - от remaining_seconds (вычитание)
	const startTimerFromSeconds = (remainingSeconds) => {
		stopTimer();
		let totalSeconds = remainingSeconds;

		const update = () => {
			if (totalSeconds <= 0) {
				remainingTime.value = "00:00:00";
				stopTimer();
				return;
			}

			totalSeconds--;
			remainingTime.value = formatSeconds(totalSeconds);
		};

		update();
		timer = setInterval(update, 1000);
	};

	const stopTimer = () => {
		if (timer) {
			clearInterval(timer);
			timer = null;
		}
	};

	// ---------- main logic ----------
	const initTimer = async () => {
		const storeQuizMatches = useQuizMatchesStore();
		const storeRequests = useRequestsStore();
		
		// Для авторизованных — получаем данные с сервера
		if (storeRequests.isAuthenticated) {
			const serverData = await storeQuizMatches.getGameMatch3();

			if (serverData && serverData.is_complete) {
				gameData.value = serverData;
				isPaused.value = serverData.is_timer_paused;

				if (serverData.is_timer_paused) {
					// Таймер на паузе — показываем статичное время
					remainingTime.value = formatSeconds(serverData.remaining_seconds);
				} else {
					// Таймер идет — запускаем локальный отсчет
					startTimerFromSeconds(serverData.remaining_seconds);
					// startTimerFromExpiry(serverData.gift_expires_at);
				}
				return;
			}
		}

		// Для неавторизованных — используем localStorage
		const stored = getStoredGameData();
		if (stored) {
			gameData.value = stored;
			isPaused.value = false;
			startTimerFromExpiryDate(stored.gift_expires_at);
		}
	};

	// Обновление данных после ответа сервера
	const updateFromServerResponse = (data) => {
		if (!data) return;

		// saveGameData(data);
		isPaused.value = data.is_timer_paused;

		if (data.is_timer_paused) {
			stopTimer();
			remainingTime.value = formatSeconds(data.remaining_seconds);
		} else if (data.gift_expires_at) {
			startTimerFromSeconds(data.remaining_seconds);
		}
	};

	// onUnmounted(stopTimer);

	return {
		remainingTime,
		isPaused,
		gameData,
		initTimer,
		updateFromServerResponse,
		saveGameData,
		clearGameData,
		stopTimer,
		getToken,
		getStoredGameData,
	};
}
